/** Shared pieces of the meme coin pages. Token metrics arrive as % (0–100) and plain decimal strings. */
import type { ReactNode } from 'react';
import type { MemeMetrics, MemeToken } from '@platform/shared';
import { cx } from '../../components/ui';

export function Card({ title, aside, children, className }: { title?: ReactNode; aside?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cx('min-w-0 rounded-2xl border border-line bg-panel px-4 py-4', className)}>
      {(title || aside) && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          {typeof title === 'string' ? <h2 className="m-0 text-[14px] font-semibold">{title}</h2> : title}
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

/** Token image, or an initials disc coloured from the mint so every token looks distinct. */
export function TokenAvatar({ token, size = 36 }: { token: Pick<MemeToken, 'symbol' | 'mint' | 'imageUrl'>; size?: number }) {
  if (token.imageUrl) {
    return <img src={token.imageUrl} alt="" width={size} height={size} loading="lazy" referrerPolicy="no-referrer" className="shrink-0 rounded-full bg-raised object-cover" style={{ width: size, height: size }} />;
  }
  let h = 0;
  for (const c of token.mint) h = (h * 31 + c.charCodeAt(0)) % 360;
  return (
    <span aria-hidden="true" className="grid shrink-0 place-items-center rounded-full font-semibold text-white" style={{ width: size, height: size, fontSize: size * 0.34, background: `hsl(${h} 55% 42%)` }}>
      {token.symbol.slice(0, 2).toUpperCase()}
    </span>
  );
}

const TONE: Record<string, string> = {
  up: 'bg-up-bg text-up',
  warn: 'bg-warn-bg text-warn',
  down: 'bg-down-bg text-down',
  muted: 'bg-raised text-muted',
};

export function StateBadge({ label, tone }: { label: string; tone: 'up' | 'warn' | 'down' | 'muted' }) {
  return <span className={cx('shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-bold tracking-wide uppercase', TONE[tone])}>{label}</span>;
}

const RISK_TONE = (r: number) => (r <= 3 ? 'bg-up-bg text-up' : r <= 6 ? 'bg-warn-bg text-warn' : 'bg-down-bg text-down');

export function RiskChip({ score }: { score: number | null }) {
  if (score === null) return <span className="rounded-full bg-raised px-2 py-0.5 text-[11px] font-semibold text-muted">Risk —</span>;
  return (
    <span className={cx('num rounded-full px-2 py-0.5 text-[11px] font-semibold', RISK_TONE(score))} title="Risk score 1–10 from the token's on-chain facts: holder concentration, developer holding, liquidity, authorities and RugCheck">
      Risk {score}/10
    </span>
  );
}

/** A percentage of supply, red when it is a warning sign. */
export function Share({ label, v, warnAt }: { label: string; v: number | null; warnAt: number }) {
  return (
    <span className="whitespace-nowrap text-muted">
      {label}{' '}
      <b className={cx('num font-semibold', v === null ? 'text-muted' : v >= warnAt ? 'text-down' : 'text-fg')}>{v === null ? '—' : `${v < 10 ? v.toFixed(1).replace(/\.0$/, '') : Math.round(v)}%`}</b>
    </span>
  );
}

/** Top 10, dev, snipers, insiders, bundlers and the LP / authority checks, as a compact strip. */
export function RiskStrip({ m, riskScore, className }: { m: MemeMetrics; riskScore: number | null; className?: string }) {
  const flag = (ok: boolean | null, label: string) => (
    <span className={cx('whitespace-nowrap', ok === true ? 'text-up' : ok === false ? 'text-down' : 'text-muted')}>
      {label} <b className="font-semibold">{ok === true ? '✓' : ok === false ? '✗' : '?'}</b>
    </span>
  );
  return (
    <div aria-label="Token risk" className={cx('flex flex-wrap gap-x-3 gap-y-1 text-[11.5px]', className)}>
      <RiskChip score={riskScore} />
      <Share label="Top 10" v={m.top10Pct} warnAt={30} />
      <Share label="Dev" v={m.devPct} warnAt={10} />
      <Share label="Snipers" v={m.snipersPct} warnAt={10} />
      {m.insidersPct !== null && <Share label="Insiders" v={m.insidersPct} warnAt={10} />}
      {flag(m.lpBurnedOrLocked, 'LP locked')}
      {flag(m.mintAuthorityRevoked === null ? null : m.mintAuthorityRevoked && m.freezeAuthorityRevoked === true, 'Authorities revoked')}
    </div>
  );
}

/** Signed % from a % value (not a fraction), coloured. */
export function ChangePct({ v, className }: { v: number | null; className?: string }) {
  if (v === null) return <span className={cx('num text-muted', className)}>—</span>;
  return <span className={cx('num', v > 0 ? 'text-up' : v < 0 ? 'text-down' : 'text-muted', className)}>{`${v > 0 ? '+' : ''}${v.toFixed(1)}%`}</span>;
}

/** "12s", "14m", "3h", "2d" since a time. */
export function age(iso: string | null): string {
  if (!iso) return '—';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export const RISK_WARNING =
  'Meme coin CFDs are extremely risky and you can lose your whole margin quickly. You trade our quoted price, not a trade on a DEX; you never own the coin.';

export function RiskWarning({ className }: { className?: string }) {
  return <p className={cx('m-0 text-[11.5px] leading-relaxed text-muted', className)}>{RISK_WARNING}</p>;
}
